import type { RulebookDisplayedSnapshot } from "@next-platform/client-common-core";
import { useTranslation } from "@next-platform/client-common-core";
import type { HudTileText } from "@next-platform/client-common-core/rulebook/hud/hudTileText";
import { Icon, IconSize, IconVariants } from "@next-platform/client-common-spicy-ui";
import type { PublicMeterView } from "@next-platform/packages-ts-types";
import React, { useCallback } from "react";

import Trans from "@/components/Trans/Trans";
import { cn } from "@/utils";

import { HUD_LINE, HUD_MUTED, HUD_STRIP_BG } from "./hudClassNames";
import RulebookMeterTile from "./RulebookMeterTile";

export type HudPanelMeter = { meter: PublicMeterView; value: number; text: HudTileText };

type RulebookMeterPanelProps = {
  meters: HudPanelMeter[];
  snapshot: RulebookDisplayedSnapshot;
  isArrived: boolean;
  isMobile: boolean;
  onOpenPanel: () => void;
  onMinimize: () => void;
  "data-testid"?: string;
};

/**
 * The meters at the top of the chat. Mobile: a compact 2×2 grid under the header. Desktop: a row of four score tiles
 * in the chat column. The whole panel opens the "what moves these meters" sheet; the arrow collapses it into a chip.
 */
const RulebookMeterPanel = ({
  meters,
  snapshot,
  isArrived,
  isMobile,
  onOpenPanel,
  onMinimize,
  "data-testid": testId = "RulebookMeterPanel",
}: RulebookMeterPanelProps) => {
  const { t } = useTranslation();
  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLDivElement>) => {
      // Keys pressed on the minimize button bubble up here; only the panel itself opens the sheet.
      if (event.target === event.currentTarget && (event.key === "Enter" || event.key === " ")) {
        event.preventDefault();
        onOpenPanel();
      }
    },
    [onOpenPanel],
  );
  const handleMinimize = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      event.stopPropagation();
      onMinimize();
    },
    [onMinimize],
  );

  return (
    <div data-testid={testId} className={cn("shrink-0", isMobile ? `border-b ${HUD_LINE} ${HUD_STRIP_BG}` : "px-4 pt-0.5")}>
      <div
        role="button"
        tabIndex={0}
        data-testid={`${testId}-Open`}
        title={t("chat:rulebook.hud.openSheet", { defaultValue: "See what moves these meters" })}
        onClick={onOpenPanel}
        onKeyDown={handleKeyDown}
        className={cn("mx-auto flex w-full cursor-pointer items-center gap-3 py-1.5 pl-3 pr-2", isMobile ? "" : "max-w-[640px] px-0")}
      >
        <div className={cn("grid min-w-0 flex-1 gap-y-2.5", isMobile ? "grid-cols-2 gap-x-4" : "grid-cols-4 gap-x-3")}>
          {meters.map(({ meter, value, text }) => (
            <RulebookMeterTile
              key={meter.id}
              meter={meter}
              value={value}
              text={text}
              snapshot={snapshot}
              isArrived={isArrived}
              variant={isMobile ? "grid" : "tile"}
              testIdPrefix={testId}
            />
          ))}
        </div>
        <div className="flex flex-none flex-col items-center gap-1">
          <button
            type="button"
            data-testid="RulebookHud-MinimizeButton"
            aria-label={t("chat:rulebook.hud.minimize", { defaultValue: "Minimize meters" })}
            title={t("chat:rulebook.hud.minimize", { defaultValue: "Minimize meters" })}
            onClick={handleMinimize}
            className={`flex h-6 w-6 items-center justify-center rounded-full border-0 bg-transparent hover:bg-gray-5 hover:text-foreground dark:hover:bg-gray-4 ${HUD_MUTED}`}
          >
            <Icon icon={IconVariants.CHEVRON_UP} size={IconSize.SMALL} />
          </button>
          {snapshot.isPastState ? (
            <span data-testid="RulebookMeterPanel-PastStateMarker" className={`whitespace-nowrap text-[10px] ${HUD_MUTED}`}>
              <Trans i18nKey="chat:rulebook.hud.pastState" defaults="Past state" />
            </span>
          ) : null}
        </div>
      </div>
    </div>
  );
};

export default React.memo(RulebookMeterPanel);
